import { useEffect, useState, useMemo } from 'react';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { useRealTimeMetrics } from '../../hooks/useRealTimeMetrics';
import { useDateRange } from '../../hooks/useDateRange';

export default function DataSourcesView() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const { days } = useDateRange();
  const [dataSources, setDataSources] = useState<any>(null);
  const isGa4Configured = Boolean(import.meta.env.VITE_GA4_MEASUREMENT_ID);

  const { data, isLoading } = useRealTimeMetrics({
    endpoint: 'data-sources',
    interval: 30000,
    days,
  });

  // Memoize sorted data sources with limit for performance
  const sortedDataSources = useMemo(() => {
    if (!data?.sources) return null;

    const normalizedSources = data.sources.map((source: any) => {
      if (String(source?.name || '').toLowerCase() !== 'ga4') {
        return source;
      }

      return {
        ...source,
        status: isGa4Configured ? 'active' : 'inactive',
        note: isGa4Configured
          ? source.note
          : 'GA4_MEASUREMENT_ID not configured - analytics disabled',
      };
    });

    return {
      ...data,
      sources: normalizedSources
        .slice()
        .sort((a: any, b: any) => {
          // Sort by status (active first) then by last update
          if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
          return new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime();
        })
        .slice(0, 50), // Limit to 50 sources for performance
    };
  }, [data?.sources, isGa4Configured]);

  useEffect(() => {
    if (sortedDataSources) {
      setDataSources(sortedDataSources);
    }
  }, [sortedDataSources]);

  if (user?.role === 'superadmin' && !selectedTenantId) {
    return (
      <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900">
        Please select a tenant from the dropdown above to view data.
      </div>
    );
  }

  return (
    <div>
      <div className="dashboard-header mb-6">
        <h1 className="text-3xl font-bold text-slate-900 m-0">Data Sources</h1>
        <p className="text-sm text-slate-500 mt-1">Monitor integration health and sync status</p>
      </div>

      {user?.role === 'superadmin' && !selectedTenantId && (
        <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900">
          Please select a tenant from the dropdown above to view data.
        </div>
      )}

      {isLoading && !dataSources ? (
        <div className="p-6 text-center text-slate-500">
          <div className="loading-spinner inline-block w-6 h-6 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin" />
          <p className="mt-3">Loading data sources...</p>
        </div>
      ) : (
        <div className="dashboard-card p-6">
          {dataSources?.sources?.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Last Update</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {dataSources.sources.map((s: any, idx: number) => (
                  <tr key={idx}>
                    <td><span className="font-medium text-slate-900">{s.name}</span></td>
                    <td>
                      <span className={`badge-${s.status || 'inactive'}`}>
                        {s.status || 'unknown'}
                      </span>
                    </td>
                    <td className="text-slate-500 text-sm">
                      {s.lastUpdate ? new Date(s.lastUpdate).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="text-slate-500 text-sm">{s.note || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-10 text-slate-400">
              No data sources available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
